import { useState, useEffect } from 'react';
import type { Ticket, Tech, JobTag } from '../../types/ticket';
import { TAG_CATEGORIES } from '../../types/ticket';
import { AppButton, AppChip, Box } from '../ui';
import { TicketActionsMenu } from '../TicketActionsMenu/TicketActionsMenu';
import { formatTimeAgo } from '../../utils/dateUtils';
import { calculatePriorityScore, getPriorityLevel } from '../../utils/priorityScore';
import { useUserStatus } from '../../contexts/UserStatusContext';
import './TicketCard.css';

interface TicketCardProps {
  ticket: Ticket;
  tech?: Tech | null;
  isHighlighted?: boolean;
  isCollapsed?: boolean;
  onStart?: (ticketId: string) => void;
  onHold?: (ticketId: string, reason?: string) => void;
  onComplete?: (ticketId: string, notes?: string) => void;
  onReopen?: (ticketId: string) => void;
  onReassign?: (ticketId: string) => void;
  onChangePriority?: (ticketId: string) => void;
  onOpenFullView?: (ticketId: string) => void;
}

export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  tech,
  isHighlighted = false,
  isCollapsed: initialCollapsed = false,
  onStart,
  onHold,
  onComplete,
  onReopen,
  onReassign,
  onChangePriority,
  onOpenFullView,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const { activeJob, startJob } = useUserStatus();

  useEffect(() => setIsCollapsed(initialCollapsed), [initialCollapsed]);

  const isMainCategoryTag = (tag: JobTag): boolean => {
    return TAG_CATEGORIES.some((category) => category.tags[0] === tag);
  };

  const priorityScore = calculatePriorityScore(ticket);
  const priorityLevel = getPriorityLevel(priorityScore);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isCompleted = ticket.status === 'Resolved';
  const isOnHold = ticket.status === 'Waiting';
  const isThisTicketActive = activeJob?.ticketId === ticket.id;
  const canStartJob = !activeJob && !isCompleted && !isOnHold;

  return (
    <Box className={`ticket-card ${isHighlighted ? 'ticket-card--highlighted' : ''} ${isCollapsed ? 'ticket-card--collapsed' : ''} ${isCompleted ? 'ticket-card--completed' : ''}`}>
      <div className="ticket-card__top" onClick={toggleCollapse}>
        <button
          className="ticket-card__collapse-btn"
          onClick={(e) => {
            e.stopPropagation();
            toggleCollapse();
          }}
          aria-label={isCollapsed ? 'Expand ticket' : 'Collapse ticket'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`ticket-card__collapse-icon ${isCollapsed ? 'ticket-card__collapse-icon--collapsed' : ''}`}
          >
            <path
              d="M4 6L8 10L12 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <a
          className="ticket-card__ticket-link"
          href={`https://servicenow.com/ticket/${ticket.ticketNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open in ServiceNow"
        >
          {ticket.ticketNumber}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M12 3L3 12M12 3H6M12 3v6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
        <AppChip chipVariant="priority" priority={ticket.priority} label={ticket.priority} size="small" />
        <div style={{ position: 'relative', marginLeft: 'auto', zIndex: 1001 }} onClick={(e) => e.stopPropagation()}>
          <TicketActionsMenu
            ticketId={ticket.id}
            ticketNumber={ticket.ticketNumber}
            currentStatus={ticket.status}
            onHold={onHold}
            onComplete={onComplete}
            onReopen={onReopen}
          />
        </div>
      </div>

      <h3 className="ticket-card__title">{ticket.shortDescription}</h3>

      {isHighlighted && (
        <div className="ticket-card__highlight-badge">
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M8 1l2.163 4.382L15 6.097l-3.5 3.41.826 4.819L8 12.207l-4.326 2.119.826-4.819L1 6.097l4.837-.715L8 1z" />
          </svg>
          Recommended Next Ticket
        </div>
      )}

      <div className="ticket-card__collapsible-content">
        <div className="ticket-card__collapsible-inner">
          <div className="ticket-card__tags">
          {(ticket.tags ?? []).map((tag) => (
            <AppChip
              key={tag}
              chipVariant="category"
              label={tag}
              size="small"
              sx={isMainCategoryTag(tag) ? { fontWeight: 600 } : undefined}
            />
          ))}
        </div>

        <div className="ticket-card__meta">
          <div className="ticket-card__meta-item">
            <span className="ticket-card__meta-label">Location</span>
            <span className="ticket-card__meta-value">{ticket.location}</span>
          </div>
          {tech && (
            <div className="ticket-card__meta-item">
              <span className="ticket-card__meta-label">Assigned To</span>
              <span className="ticket-card__meta-value">{tech.name}</span>
            </div>
          )}
          <div className="ticket-card__meta-item">
            <span className="ticket-card__meta-label">Age</span>
            <span className="ticket-card__meta-value">{formatTimeAgo(ticket.createdAt)}</span>
          </div>
        </div>

        <div className="ticket-card__urgency">
          <div className="ticket-card__urgency-header">
            <span className="ticket-card__urgency-label">Urgency Score</span>
            <span className="ticket-card__urgency-value">{priorityScore}/100</span>
          </div>
          <div className="ticket-card__urgency-bar">
            <div
              className="ticket-card__urgency-fill"
              style={{
                width: `${priorityScore}%`,
                backgroundColor: priorityLevel.color,
              }}
            />
          </div>
        </div>

        <div className="ticket-card__actions">
          {canStartJob && (
            <AppButton
              variant="primary"
              size="small"
              onClick={() => {
                startJob(ticket.id, ticket.ticketNumber);
                onStart?.(ticket.id);
              }}
              startIcon={
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4 2l10 6-10 6V2z" />
                </svg>
              }
            >
              Start Job
            </AppButton>
          )}

          {isThisTicketActive && (
            <AppChip
              chipVariant="status"
              active
              label="Active Job"
              size="small"
              icon={
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                  <circle cx="8" cy="8" r="4" />
                </svg>
              }
            />
          )}

          {activeJob && !isThisTicketActive && !isCompleted && !isOnHold && (
            <span className="ticket-card__waiting-label">
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="currentColor"
                style={{ marginRight: '4px', opacity: 0.6 }}
              >
                <circle cx="8" cy="8" r="6" opacity="0.3" />
              </svg>
              In Queue
            </span>
          )}

          {onReassign && (
            <AppButton
              variant="ghost"
              size="small"
              onClick={() => onReassign(ticket.id)}
            >
              Reassign
            </AppButton>
          )}

          {onOpenFullView && (
            <AppButton
              variant="ghost"
              size="small"
              onClick={() => onOpenFullView(ticket.id)}
              startIcon={
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 8.5V14a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1h5.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 2h4v4M7 9l7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            >
              View Details
            </AppButton>
          )}
        </div>
        </div>
      </div>
    </Box>
  );
};
